import type { BillingCheckoutConfig } from "./api.js";

/**
 * Loading Maxio.js, and refusing to.
 *
 * Card data must never touch our services, so the card fields are not our
 * fields: Maxio.js draws them as iframes served from Maxio's origin, the
 * numbers are typed into those, and what comes back to us is a one-time token
 * worth nothing once spent. `apps/platform/src/routes/billing.ts` exchanges
 * that token; this file is everything that happens before it exists.
 *
 * **The control this file exists for is the refusal.** A third-party script on
 * a page every signed-in customer can reach is a real piece of attack surface,
 * and dev2, CI and every environment before the Maxio catalog exists (LGN-88)
 * have no use for it whatsoever. So the script tag is added only when the
 * platform said there is something to initialise it with, and never otherwise:
 * no probe, no preload, no tag. `checkout.test.ts` states that bypass and
 * asserts the refusal.
 *
 * Everything takes a `Document` rather than reaching for the global one. The
 * repository's Vitest environment is `node` and has no DOM at all, which is
 * what makes the guard testable: a recording stub stands in for the document
 * and the test asserts that nothing was appended to it.
 */

/** The corner of the DOM this module needs. Narrow so a test can stand in for it. */
export interface ScriptHost {
  createElement(tagName: "script"): ScriptElement;
  querySelector(selectors: string): ScriptElement | null;
  readonly head: { appendChild(node: ScriptElement): void };
}

export interface ScriptElement {
  src: string;
  async: boolean;
  crossOrigin: string | null;
  setAttribute(name: string, value: string): void;
  addEventListener(type: "load" | "error", listener: () => void): void;
}

/**
 * The selector that finds a tag this module already added.
 *
 * A data attribute rather than matching on `src`: the URL is configuration and
 * a selector matching a URL would quietly stop finding the tag the day the
 * vendor path changes, and adding Maxio.js twice puts two sets of iframes into
 * one form.
 */
const MARKER = "data-lagoon-checkout";

/**
 * Ensure Maxio.js is on the page, or decide that it must not be.
 *
 * Returns the script element when one is present or has just been added, and
 * `null` when the answer is "do not load it". Null is not a failure: it is the
 * ordinary state of an environment with no billing, and the caller renders the
 * plan and the meters without a card form.
 *
 * Idempotent by the marker attribute, because React will call this again on
 * every render that changes anything, and a second Maxio.js on the page is two
 * tokenisers competing for the same form.
 */
export function ensureCheckoutScript(
  doc: ScriptHost,
  checkout: BillingCheckoutConfig | null,
): ScriptElement | null {
  /**
   * The guard, and the whole point of the module.
   *
   * Delete this line and the test that says "loads nothing when billing is not
   * configured" goes red, which is what §8 asks of a control: the bypass is
   * "render the billing page in an environment with no Maxio", and the refusal
   * asserted is that nothing was appended to the document.
   */
  if (checkout === null) return null;

  const existing = doc.querySelector(`script[${MARKER}]`);
  if (existing !== null) return existing;

  const script = doc.createElement("script");
  /**
   * The exact URL the platform handed over, never one built here.
   *
   * It comes from `MAXIO_JS_SCRIPT_URL` in `@lagoon/billing`, which is the same
   * constant the CSP's allowed origin is derived from, so the policy and the
   * tag cannot drift apart into a load that is silently blocked in the browser
   * and invisible on the server.
   */
  script.src = checkout.scriptUrl;
  script.async = true;
  /**
   * No `crossorigin` and no integrity hash, and the reason is worth stating.
   *
   * Maxio publishes one URL for Maxio.js and it ends in `/latest/`. There is no
   * versioned path, so there is no stable content to hash: an SRI attribute
   * over a URL the vendor rotates would take the card form offline on their
   * schedule rather than ours. `crossorigin="anonymous"` was tried first so
   * that `integrity` could be added later, and js.chargify.com answers with
   * no Access-Control-Allow-Origin header, so the browser refused the script
   * and the card form never loaded (dev2 against sb-blackpearl, 2026-09-03).
   * A classic script tag is what the vendor supports. What is available is
   * applied instead: the origin is allowlisted and nothing else is, and the
   * tag exists only on this screen and only when billing is configured.
   */
  script.setAttribute(MARKER, "");
  doc.head.appendChild(script);
  return script;
}

/**
 * The document, when there is one.
 *
 * The screen calls this rather than naming `document` inline, so that the one
 * place this module touches a global is one function with a guard on it.
 * Server-side rendering is not a thing this SPA does, but a module that throws
 * on import outside a browser is a module no test can reach.
 *
 * Narrowed rather than asserted. `document as unknown as ScriptHost` would
 * compile and would also compile on the day {@link ScriptHost} grew a method
 * `Document` does not have; checking for the two methods this module calls
 * costs nothing on a path that runs once per mount.
 */
export function browserScriptHost(): ScriptHost | null {
  const found: unknown = Reflect.get(globalThis, "document");
  return isScriptHost(found) ? found : null;
}

function isScriptHost(value: unknown): value is ScriptHost {
  if (typeof value !== "object" || value === null) return false;
  return (
    typeof Reflect.get(value, "createElement") === "function" &&
    typeof Reflect.get(value, "querySelector") === "function"
  );
}
